'use client';

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  registerUserScopedStore,
  userScopedStorage,
} from '@/shared/lib/user-scoped-storage';

export type FeedbackVariant = 'positive' | 'filtered';

/**
 * Each variant has its own resting state, and only a departure from it is
 * stored. The architect's note (`positive`) rests collapsed to its icon on the
 * render: it is delivered by the AI architect when the render finishes
 * (docs/assistant/ADR-001, «Реплика о рендере»), and is reread only on demand.
 * The `filtered` warning rests expanded: it says what did not make it into
 * the design, and the user must see it.
 */
const COLLAPSED_BY_DEFAULT: Record<FeedbackVariant, boolean> = {
  positive: true,
  filtered: false,
};

// A positive key marks an expanded note; the suffix keeps keys stored back
// when it meant "collapsed" from flipping meaning.
const keyOf = (renderId: string, variant: FeedbackVariant) =>
  `${renderId}:${variant === 'positive' ? 'positive-open' : variant}`;

interface FeedbackCollapseState {
  /** Keys of feedback toggled away from its variant's resting state. */
  collapsedIds: string[];
  isCollapsed: (renderId: string, variant: FeedbackVariant) => boolean;
  setCollapsed: (
    renderId: string,
    variant: FeedbackVariant,
    collapsed: boolean
  ) => void;
  toggle: (renderId: string, variant: FeedbackVariant) => void;
}

export const useFeedbackCollapseStore = create<FeedbackCollapseState>()(
  persist(
    (set, get) => ({
      collapsedIds: [],
      isCollapsed: (renderId, variant) =>
        get().collapsedIds.includes(keyOf(renderId, variant)) !==
        COLLAPSED_BY_DEFAULT[variant],
      setCollapsed: (renderId, variant, collapsed) => {
        const key = keyOf(renderId, variant);
        const stored = collapsed !== COLLAPSED_BY_DEFAULT[variant];
        set((state) => {
          const has = state.collapsedIds.includes(key);
          if (stored && !has) {
            return { collapsedIds: [...state.collapsedIds, key] };
          }
          if (!stored && has) {
            return {
              collapsedIds: state.collapsedIds.filter((k) => k !== key),
            };
          }
          return state;
        });
      },
      toggle: (renderId, variant) => {
        const key = keyOf(renderId, variant);
        set((state) => ({
          collapsedIds: state.collapsedIds.includes(key)
            ? state.collapsedIds.filter((k) => k !== key)
            : [...state.collapsedIds, key],
        }));
      },
    }),
    {
      name: 'render-feedback-collapse',
      // User-scoped key prefix: see `user-scoped-storage.ts`.
      storage: createJSONStorage(() => userScopedStorage()),
    }
  )
);

registerUserScopedStore(useFeedbackCollapseStore);
